import {
  Timeline,
  TimelineContent,
  TimelineDate,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { MessageCircle, CloudDownload, Check, Plus, Square } from "lucide-react"
import { format, parseISO, isPast } from "date-fns"
import { usePage, useForm } from "@inertiajs/react"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { getCircularIndex } from "@/lib/utils"
import type { Deal } from "@/types/serializers"
import { StatusBadge } from "@/components/status-badges"

interface DealUpdate {
  id: number
  notes: string
  request_type: string
  completed_at: string | null
  created_at: string
  resolved_at: string | null
}

interface PageProps {
  deal: Deal,
  deal_updates: DealUpdate[]
  [key: string]: any
}

interface UpdateFormProps {
  dealId: number
}

const requestOptions = {
  "Activate": "Setup the deal in accounting",
  "Extend": "Take the next extension",
  "Expire": "Set to expire at the end of term — no more extensions",
  "Archive": "Move deal to archive"
}

function UpdateForm({ dealId }: UpdateFormProps) {
  const { data, setData, post, processing, reset, transform } = useForm({
    notes: "",
    request_type: "None"
  })

  transform((data) => ({
    ...data,
    request_type: data.request_type == 'None' ? null : data.request_type
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/deals/${dealId}/deal_updates`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
      }
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <Textarea
        placeholder="Optional notes..."
        className="min-h-[100px] mt-2"
        value={data.notes}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setData("notes", e.target.value)
        }}
      />
      <div className="flex-1">
        <div className="flex items-center gap-2 pt-4 justify-end">
          <label className="text-sm font-medium">
            <HoverCard>
              <HoverCardTrigger className="underline underline-dashed">Request Action</HoverCardTrigger>
              <HoverCardContent className="w-fit whitespace-nowrap">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                  {Object.entries(requestOptions).map(([key, description]) => (
                    <div key={key} className="contents">
                      <div className="font-medium">{key}</div>
                      <div className="text-muted-foreground">{description}</div>
                    </div>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          </label>
          <Select value={data.request_type} onValueChange={(value: string) => {
            setData("request_type", value)
          }}>
            <SelectTrigger>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="None">None</SelectItem>
              <SelectSeparator />
              <SelectItem value="activate">Activate</SelectItem>
              <SelectItem value="extend">Extend</SelectItem>
              <SelectItem value="expire">Expire</SelectItem>
              <SelectSeparator />
              <SelectItem value="archive">Archive</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={processing}>
            {processing ? "Sending..." : "Send Note/Request"}
          </Button>
        </div>
      </div>
    </form>
  )
}

function formatDate(dateString: string): string {
  return format(parseISO(dateString), 'MMM dd, yyyy')
}

export default function UpdateTimeline() {
  const { deal, deal_updates } = usePage<PageProps>().props
  const { patch } = useForm()

  const toggleCompleted = (updateId: number) => {
    patch(`/deals/${deal.id}/deal_updates/${updateId}/toggle_resolved`, {
      preserveScroll: true,
      preserveState: true
    })
  }

  const getTimelineItems = () => {
    const items: Array<{
      id: string | number;
      title: React.ReactNode;
      description?: React.ReactNode;
      date?: React.ReactNode;
      icon: React.ReactNode;
      isCompleted: boolean;
      hasRequest?: boolean;
    }> = [];

    const names = [
      "Aria Blackwood",
      "Marcus Chen",
      "Zara Patel",
      "Lucas Fitzgerald",
      "Sofia Rodriguez"
    ];

    // Add update form as first item
    items.push({
      id: 'new-update',
      title: "Add a note and/or request",
      description: <UpdateForm dealId={deal.id} />,
      icon: <Plus className="h-4 w-4" />,
      isCompleted: false
    });

    // Add deal updates
    if (deal_updates && deal_updates.length > 0) {
      deal_updates.forEach((update, i) => {
        const updateType = update.request_type === null ? "noted:" : "requested";
        const requestBadge = update.request_type === null ? null : <StatusBadge status={{type: 'updateRequest', status: update.request_type as any}} className="ml-1" />;

        console.log(update)
        items.push({
          id: update.id,
          date: (<>
            { formatDate(update.created_at) }
            {update.resolved_at !== null && <span className="text-muted-foreground"> <span className="text-muted">●</span> Resolved {formatDate(update.resolved_at)}</span> }
          </>),
          title: (<>
            <span>{getCircularIndex(names, i)} <span className="text-muted-foreground">{updateType}</span> </span>
            {requestBadge}
          </>),
          description: (<span className="whitespace-pre-wrap">{update.notes}</span>),
          hasRequest: true,
          isCompleted: update.resolved_at !== null,
          icon: <Check strokeWidth={3} className="h-4 w-4" />
        });
      });
    }

    // Add created_at as last item
    items.push({
      id: 'imported',
      date: formatDate(deal.created_at),
      title: (<span>Imported <span className="text-muted-foreground">from Salesforce</span></span>),
      isCompleted: true,
      icon: <CloudDownload strokeWidth={3} className="h-4 w-4" />
    });

    return items;
  };

  return (
    <Timeline>
      {getTimelineItems().map((item) => (
        <TimelineItem
          key={item.id}
          className={`
            group-data-[orientation=vertical]/timeline:ms-10
            group-data-[orientation=vertical]/timeline:first:pb-8
          `}
          data-completed={item.isCompleted || undefined}
          data-has-request={item.hasRequest || undefined}
        >
          <TimelineHeader>
            <TimelineSeparator className={`
              group-data-[orientation=vertical]/timeline:-left-7
              group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)]
              group-data-[orientation=vertical]/timeline:translate-y-6.5
            `} />
            <TimelineTitle className="sm:-mt-0.5 pt-1">{item.title}</TimelineTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <TimelineIndicator
                  className={`
                    group-data-completed/timeline-item:bg-primary
                    group-data-completed/timeline-item:text-primary-foreground
                    group-data-completed/timeline-item:border-none

                    group-data-has-request/timeline-item:cursor-pointer
                    group-data-has-request/timeline-item:group-data-completed/timeline-item:bg-emerald-500
                    group-data-has-request/timeline-item:group-data-completed/timeline-item:text-white
                    group-data-has-request/timeline-item:group-data-completed/timeline-item:border-emerald-500
                    group-data-has-request/timeline-item:group-data-completed/timeline-item:hover:bg-amber-500
                    group-data-has-request/timeline-item:group-data-completed/timeline-item:hover:border-amber-500

                    group-data-has-request/timeline-item:group-not-data-[completed]/timeline-item:hover:bg-emerald-500
                    group-data-has-request/timeline-item:group-not-data-[completed]/timeline-item:hover:text-white
                    group-data-has-request/timeline-item:group-not-data-[completed]/timeline-item:hover:border-emerald-500

                    group-data-[orientation=vertical]/timeline:-left-7
                    flex size-6 items-center justify-center
                    text-primary/50
                  `}
                  onClick={() => item.hasRequest && toggleCompleted(item.id)}
                >
                  {item.icon}
                </TimelineIndicator>
              </TooltipTrigger>
              {item.hasRequest && (
                <TooltipContent>{item.isCompleted ? "Mark as Unresolved" : "Mark as Resolved"}</TooltipContent>
              )}
            </Tooltip>
          </TimelineHeader>
          <TimelineContent>{item.description}</TimelineContent>
          <TimelineDate className="text-xs text-muted-foreground mt-1">
            {item.date}
          </TimelineDate>
        </TimelineItem>
      ))}
    </Timeline>
  )
}
